import { exportToBlob } from "@excalidraw/excalidraw";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const S3_BUCKET = import.meta.env.VITE_S3_BUCKET || "";
const S3_REGION = import.meta.env.VITE_S3_REGION || "us-east-2";

const MAX_THUMB_SIZE = 512; // px, square

/**
 * Sanitize an id so it is safe to use as an S3 key segment.
 */
const sanitizeId = (id: string | undefined | null): string => {
  if (!id) return "";
  return (
    id
      .toString()
      .trim()
      // replace any non-word characters with '-'
      .replace(/[^a-zA-Z0-9-_]/g, "-")
      // collapse multiple '-'
      .replace(/-+/g, "-")
      // limit length
      .slice(0, 64)
  );
};

/**
 * Resize and optionally center-crop a Blob image to maxWidth x maxHeight.
 * Returns a PNG Blob.
 */
async function resizeAndCropBlob(
  blob: Blob,
  maxWidth: number,
  maxHeight: number,
  backgroundColor = "#ffffff",
): Promise<Blob> {
  // Yield to browser before starting
  await new Promise((resolve) => setTimeout(resolve, 0));

  // Create image bitmap if available for faster decoding
  let bitmap: ImageBitmap | null = null;
  try {
    // createImageBitmap can throw for some blobs in some environments
    // but when available it's faster and uses the browser decoder.
    // @ts-ignore
    if (typeof createImageBitmap === "function") {
      // @ts-ignore
      bitmap = await createImageBitmap(blob as any);
    }
  } catch (_) {
    bitmap = null;
  }

  if (!bitmap) {
    // Fallback to using HTMLImageElement
    const img = await blobToImage(blob);
    // draw image into an offscreen canvas
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    bitmap = await createImageBitmap(img, 0, 0, iw, ih);
  }

  // Yield again after image decode
  await new Promise((resolve) => setTimeout(resolve, 0));

  const iw = bitmap.width;
  const ih = bitmap.height;

  // Calculate center-crop box to preserve aspect ratio, then scale to fit
  const targetRatio = maxWidth / maxHeight;
  const srcRatio = iw / ih;

  let sx = 0;
  let sy = 0;
  let sWidth = iw;
  let sHeight = ih;

  if (srcRatio > targetRatio) {
    // source is wider than target: crop horizontally
    sWidth = Math.round(ih * targetRatio);
    sx = Math.round((iw - sWidth) / 2);
  } else if (srcRatio < targetRatio) {
    // source is taller than target: crop vertically
    sHeight = Math.round(iw / targetRatio);
    sy = Math.round((ih - sHeight) / 2);
  }

  const canvas = document.createElement("canvas");
  canvas.width = maxWidth;
  canvas.height = maxHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  // Disable image smoothing for crisp rendering
  ctx.imageSmoothingEnabled = false;

  // Fill background for opaque thumbnail (so dark theme thumbnails don't have transparency)
  ctx.fillStyle = backgroundColor || "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // draw the cropped & scaled image
  ctx.drawImage(
    bitmap as ImageBitmap,
    sx,
    sy,
    sWidth,
    sHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  // convert to blob
  return await new Promise<Blob>((resolve) => {
    canvas.toBlob((b) => {
      // If toBlob returns null, fallback to original
      if (b) {
        resolve(b);
      } else {
        resolve(blob);
      }
    }, "image/png");
  });
}

async function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (e) => reject(e);
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
/**
 * Generates a thumbnail for a whiteboard using the Excalidraw API
 */
export const generateWhiteboardThumbnail = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
): Promise<Blob> => {
  const elements = excalidrawAPI.getSceneElements();
  const appState = excalidrawAPI.getAppState();
  const files = excalidrawAPI.getFiles();

  // Decide background color based on page/theme. Prefer explicit dark/light
  // detection so exported thumbnail matches the UI theme.
  let bgColor = appState.viewBackgroundColor || "#ffffff";
  try {
    const html = document.documentElement;
    const themeAttr = html?.getAttribute("data-theme") || html?.dataset?.theme;
    const bodyHasDark = document.body.classList.contains("dark");
    if (themeAttr === "dark" || bodyHasDark) {
      // dark background
      bgColor = "#1a1a1a";
    } else if (themeAttr === "light") {
      bgColor = "#ffffff";
    }
  } catch (e) {
    // ignore - fall back to appState.viewBackgroundColor
  }

  // Filter elements to only those in the current viewport center
  // This dramatically reduces the number of elements to render
  const viewportWidth = appState.width;
  const viewportHeight = appState.height;
  const zoom = appState.zoom.value;
  const scrollX = appState.scrollX;
  const scrollY = appState.scrollY;

  // Define capture area (center portion of viewport)
  const captureWidth = viewportWidth / zoom;
  const captureHeight = viewportHeight / zoom;
  const centerX = -scrollX;
  const centerY = -scrollY;

  const minX = centerX - captureWidth / 2;
  const maxX = centerX + captureWidth / 2;
  const minY = centerY - captureHeight / 2;
  const maxY = centerY + captureHeight / 2;

  // Filter elements that are at least partially visible in capture area
  const visibleElements = elements.filter((el) => {
    // Quick bounds check
    return !(
      el.x + el.width < minX ||
      el.x > maxX ||
      el.y + el.height < minY ||
      el.y > maxY
    );
  });

  //   console.log(
  //     `Thumbnail: rendering ${visibleElements.length} of ${elements.length} elements`,
  //   ); // eslint-disable-line no-console

  // Generate thumbnail using exportToBlob with explicit background
  // Use a smaller scale for large canvases to reduce processing time
  const rawBlob = await exportToBlob({
    elements:
      visibleElements.length > 0 ? visibleElements : elements.slice(0, 50), // Fallback to first 50 if filter fails
    appState: {
      ...appState,
      exportBackground: true,
      viewBackgroundColor: bgColor,
      exportScale: 0.5, // Reduce export scale for faster rendering
    },
    files,
    mimeType: "image/png",
    quality: 0.6, // Lower quality for thumbnails
    exportPadding: 10,
  });

  // Yield to browser before heavy resize operation
  await new Promise((resolve) => setTimeout(resolve, 0));

  // Resize / center-crop to a reasonable maximum to avoid huge thumbnails
  const normalized = await resizeAndCropBlob(
    rawBlob,
    MAX_THUMB_SIZE,
    MAX_THUMB_SIZE,
    bgColor,
  );

  return normalized;
};

/**
 * Uploads a whiteboard thumbnail to S3
 */
export const uploadWhiteboardThumbnail = async (
  whiteboardId: string,
  blob: Blob,
): Promise<string> => {
  const safeId = sanitizeId(whiteboardId) || `unknown-${Date.now()}`;
  const key = `thumbnails/${safeId}.png`;

  //   console.log("Requesting upload URL for:", key, "size:", blob.size); // eslint-disable-line no-console

  // Get presigned URL from backend
  const response = await fetch(`${API_URL}/api/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, contentType: "image/png" }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get upload URL: ${response.statusText}`);
  }

  const { uploadUrl } = await response.json();

  //   console.log("Got presigned URL, uploading to S3..."); // eslint-disable-line no-console

  // Upload directly to S3 using presigned URL
  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": "image/png" },
  });

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
  }

  console.log("Successfully uploaded thumbnail to S3"); // eslint-disable-line no-console
  // Return canonical S3 object URL
  return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${key}`;
};

/**
 * Deletes a whiteboard thumbnail from S3
 */
export const deleteWhiteboardThumbnail = async (
  whiteboardId: string,
): Promise<void> => {
  try {
    const key = `thumbnails/${
      sanitizeId(whiteboardId) || `unknown-${Date.now()}`
    }.png`;

    await fetch(`${API_URL}/api/files/${key}`, {
      method: "DELETE",
    });
  } catch (error) {
    console.warn("Failed to delete thumbnail:", error);
  }
};

/**
 * Uploads a profile picture to S3
 */
export const uploadProfilePicture = async (
  profileId: string,
  file: File,
): Promise<string> => {
  const extension = file.name.split(".").pop() || "jpg";
  const key = `profile-pictures/${
    sanitizeId(profileId) || `user-${Date.now()}`
  }.${extension}`;

  console.log(
    "Uploading profile picture:",
    key,
    "type:",
    file.type,
    "size:",
    file.size,
  ); // eslint-disable-line no-console

  // Get presigned URL from backend
  const response = await fetch(`${API_URL}/api/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, contentType: file.type }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get upload URL: ${response.statusText}`);
  }

  const { uploadUrl } = await response.json();

  console.log("Got presigned URL, uploading profile picture to S3..."); // eslint-disable-line no-console

  // Upload directly to S3 using presigned URL
  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
  }

  console.log("Successfully uploaded profile picture to S3"); // eslint-disable-line no-console

  return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${key}`;
};

/**
 * Deletes a profile picture from S3
 */
export const deleteProfilePicture = async (
  profileId: string,
): Promise<void> => {
  try {
    const extensions = ["jpg", "jpeg", "png", "gif", "webp"];
    await Promise.all(
      extensions.map(async (ext) => {
        try {
          const key = `profile-pictures/${profileId}.${ext}`;
          await fetch(`${API_URL}/api/files/${key}`, {
            method: "DELETE",
          });
        } catch {
          // Ignore errors for non-existent files
        }
      }),
    );
  } catch (error) {
    console.warn("Failed to delete profile picture:", error);
  }
};

// ============================================================================
// Whiteboard Data Storage (S3)
// ============================================================================

/**
 * Uploads whiteboard data (elements + files) to S3
 * This bypasses Firestore completely for large data
 */
export const uploadWhiteboardData = async (
  whiteboardId: string,
  elements: any[],
  files: Record<string, any>,
): Promise<string> => {
  const safeId = sanitizeId(whiteboardId) || `unknown-${Date.now()}`;
  const key = `whiteboards/${safeId}.json`;

  // Save in proper Excalidraw format so downloaded files are valid
  const excalidrawData = {
    type: "excalidraw",
    version: 2,
    source: window.location.origin,
    elements,
    appState: {
      viewBackgroundColor: "#ffffff",
      gridSize: null,
    },
    files,
  };

  const data = JSON.stringify(excalidrawData);
  const blob = new Blob([data], { type: "application/json" });

  // Get presigned URL from backend
  const response = await fetch(`${API_URL}/api/upload-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, contentType: "application/json" }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get upload URL: ${response.statusText}`);
  }

  const { uploadUrl } = await response.json();

  // Upload directly to S3 using presigned URL
  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": "application/json" },
  });

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
  }

  console.log(`[S3] Successfully uploaded whiteboard data`); // eslint-disable-line no-console
  return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${key}`;
};

/**
 * Downloads whiteboard data from S3
 */
export const downloadWhiteboardData = async (
  whiteboardId: string,
): Promise<{ elements: any[]; files: Record<string, any> } | null> => {
  const safeId = sanitizeId(whiteboardId) || `unknown`;
  const key = `whiteboards/${safeId}.json`;

  console.log(`[S3] Downloading whiteboard data: ${key}`); // eslint-disable-line no-console

  try {
    // Get presigned download URL from backend
    const urlResponse = await fetch(`${API_URL}/api/download-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    });

    if (!urlResponse.ok) {
      console.warn(
        `[S3] Failed to get download URL: ${urlResponse.statusText}`,
      ); // eslint-disable-line no-console
      return null;
    }

    const { downloadUrl } = await urlResponse.json();

    const response = await fetch(downloadUrl);
    if (!response.ok) {
      if (response.status === 404 || response.status === 403) {
        // File doesn't exist yet - return null
        return null;
      }
      throw new Error(`Failed to download: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`[S3] Successfully downloaded whiteboard data`); // eslint-disable-line no-console

    // Handle both formats: new (full excalidraw) and old (just elements/files)
    if (data.type === "excalidraw") {
      return {
        elements: data.elements || [],
        files: data.files || {},
      };
    }

    // Legacy format
    return {
      elements: data.elements || [],
      files: data.files || {},
    };
  } catch (error) {
    console.warn(`[S3] Failed to download whiteboard data:`, error); // eslint-disable-line no-console
    return null;
  }
};

/**
 * Deletes whiteboard data from S3
 */
export const deleteWhiteboardData = async (
  whiteboardId: string,
): Promise<void> => {
  try {
    const key = `whiteboards/${sanitizeId(whiteboardId)}.json`;
    await fetch(`${API_URL}/api/files/${key}`, {
      method: "DELETE",
    });
    console.log(`[S3] Deleted whiteboard data: ${key}`); // eslint-disable-line no-console
  } catch (error) {
    console.warn("[S3] Failed to delete whiteboard data:", error);
  }
};

// ============================================================================
// Local Backup Storage
// ============================================================================

/**
 * Sanitize whiteboard name for use as filename
 */
const sanitizeFilename = (name: string): string => {
  return name
    .replace(/[<>:"/\\|?*]/g, "-") // Replace invalid filename chars
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim()
    .slice(0, 200); // Limit length
};

/**
 * Saves whiteboard to local folder using File System Access API
 */
export const saveWhiteboardLocally = async (
  folderHandle: FileSystemDirectoryHandle,
  whiteboardName: string,
  elements: any[],
  files: Record<string, any>,
): Promise<void> => {
  try {
    // Verify we still have permission before attempting to write
    // This is the first point where the handle is actually used
    const permission = await (folderHandle as any).requestPermission({
      mode: "readwrite",
    });
    if (permission !== "granted") {
      throw new Error(
        "Permission denied for local backup folder. Please re-enable local backup.",
      );
    }

    const filename = `${sanitizeFilename(whiteboardName)}.excalidraw`;

    // Create proper Excalidraw file format
    const excalidrawData = {
      type: "excalidraw",
      version: 2,
      source: "excalidraw-local",
      elements,
      appState: {
        viewBackgroundColor: "#ffffff",
        gridSize: null,
      },
      files,
    };

    const data = JSON.stringify(excalidrawData, null, 2);

    // Get or create file in the folder
    const fileHandle = await folderHandle.getFileHandle(filename, {
      create: true,
    });

    // Write to file - use type assertion for File System Access API
    const writable = await (fileHandle as any).createWritable();
    await writable.write(data);
    await writable.close();

    // eslint-disable-next-line no-console
    console.log(`[LocalBackup] Saved to ${filename}`);
  } catch (error) {
    console.error("[LocalBackup] Failed to save locally:", error);
    throw error;
  }
};

/**
 * Prompts user to select a folder for local backup
 */
export const selectLocalBackupFolder =
  async (): Promise<FileSystemDirectoryHandle | null> => {
    try {
      // @ts-ignore - showDirectoryPicker is not in all TS libs
      const handle = await window.showDirectoryPicker({
        mode: "readwrite",
      });
      return handle;
    } catch (error) {
      // User cancelled or API not supported
      console.warn(
        "[LocalBackup] Folder selection cancelled or failed:",
        error,
      );
      return null;
    }
  };

const BACKUP_FOLDER_DB_NAME = "excalidraw-backup-folders";
const BACKUP_FOLDER_STORE_NAME = "folders";

/**
 * Opens or creates the IndexedDB for storing folder handles
 */
const openBackupFolderDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(BACKUP_FOLDER_DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(BACKUP_FOLDER_STORE_NAME);
    };
  });
};

/**
 * Saves a folder handle to IndexedDB for a specific profile
 */
export const saveBackupFolderHandle = async (
  profileId: string,
  handle: FileSystemDirectoryHandle,
): Promise<void> => {
  const db = await openBackupFolderDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BACKUP_FOLDER_STORE_NAME, "readwrite");
    tx.objectStore(BACKUP_FOLDER_STORE_NAME).put(handle, profileId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

/**
 * Removes a folder handle from IndexedDB for a specific profile
 */
export const removeBackupFolderHandle = async (
  profileId: string,
): Promise<void> => {
  const db = await openBackupFolderDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BACKUP_FOLDER_STORE_NAME, "readwrite");
    tx.objectStore(BACKUP_FOLDER_STORE_NAME).delete(profileId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

/**
 * Restores a folder handle from IndexedDB.
 * Permission is NOT checked here - it will be verified when actually needed (during save).
 * This ensures profile unlock is never blocked by permission prompts.
 * Returns null if handle doesn't exist.
 */
export const restoreBackupFolderHandle = async (
  profileId: string,
): Promise<FileSystemDirectoryHandle | null> => {
  try {
    const db = await openBackupFolderDB();
    const handle = await new Promise<FileSystemDirectoryHandle | undefined>(
      (resolve, reject) => {
        const tx = db.transaction(BACKUP_FOLDER_STORE_NAME, "readonly");
        const request = tx.objectStore(BACKUP_FOLDER_STORE_NAME).get(profileId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
      },
    );

    if (!handle) {
      return null;
    }

    // Return handle immediately without permission check
    // Permission will be verified when actually writing (in saveWhiteboardLocally)
    return handle;
  } catch (error) {
    console.warn("[LocalBackup] Failed to restore folder handle:", error);
    return null;
  }
};
